import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Loader2, Layers, X, Lock } from "lucide-react";

const emptyArea = () => ({ key: "", name: "", weight: 0, guide_question: "", questions: [""], anchors: { 1: "", 3: "", 5: "" } });
const emptyForm = { name: "", description: "", areas: [emptyArea()] };

export default function FrameworksPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  const load = async () => { const { data } = await api.get("/frameworks"); setItems(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", description: "", areas: [emptyArea()] }); setEditId(null); setOpen(true); };

  const openEdit = async (fid) => {
    const { data } = await api.get(`/frameworks/${fid}`);
    setForm({
      name: data.name, description: data.description || "",
      areas: (data.areas || []).map((a) => ({
        key: a.key, name: a.name, weight: a.weight, guide_question: a.guide_question || "",
        questions: a.questions?.length ? a.questions : [""],
        anchors: { 1: a.anchors?.["1"] || "", 3: a.anchors?.["3"] || "", 5: a.anchors?.["5"] || "" },
      })),
    });
    setEditId(fid); setOpen(true);
  };

  const clone = async (fid) => {
    try { await api.post(`/frameworks/${fid}/clone`); toast.success("Framework duplicato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (fid) => {
    if (!window.confirm("Eliminare questo framework?")) return;
    try { await api.delete(`/frameworks/${fid}`); toast.success("Framework eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const totalWeight = form.areas.reduce((s, a) => s + (parseInt(a.weight) || 0), 0);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nome framework obbligatorio"); return; }
    if (totalWeight !== 100) { toast.error(`La somma dei pesi deve essere 100 (attuale: ${totalWeight})`); return; }
    const payload = {
      name: form.name, description: form.description,
      areas: form.areas.map((a) => ({
        key: a.key.trim(), name: a.name, weight: parseInt(a.weight) || 0,
        guide_question: a.guide_question,
        questions: a.questions.filter((q) => q.trim()),
        anchors: { 1: a.anchors[1], 3: a.anchors[3], 5: a.anchors[5] },
      })),
    };
    setSaving(true);
    try {
      if (editId) await api.put(`/frameworks/${editId}`, payload);
      else await api.post("/frameworks", payload);
      toast.success("Framework salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const updArea = (i, patch) => setForm((f) => ({ ...f, areas: f.areas.map((a, idx) => idx === i ? { ...a, ...patch } : a) }));
  const addArea = () => setForm((f) => ({ ...f, areas: [...f.areas, emptyArea()] }));
  const rmArea = (i) => setForm((f) => ({ ...f, areas: f.areas.filter((_, idx) => idx !== i) }));

  return (
    <Layout>
      <PageHeader title="Libreria Framework" subtitle={`${items.length} framework di assessment`}>
        <Button data-testid="add-framework-btn" onClick={openNew} className="rounded-none"><Plus className="w-4 h-4 mr-2" /> Nuovo framework</Button>
      </PageHeader>

      <div className="p-8">
        {loading ? <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="frameworks-list">
            {items.map((fw, i) => (
              <div key={fw.id} data-testid={`framework-card-${i}`} className="bg-white border border-border p-5">
                <div className="flex items-start justify-between">
                  <Layers className="w-5 h-5 text-primary" />
                  {fw.is_default && <span className="text-[10px] font-bold tracking-wide bg-primary text-primary-foreground px-2 py-0.5">PREDEFINITO</span>}
                </div>
                <h3 className="font-heading font-semibold tracking-tight mt-3">{fw.name}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2 min-h-[40px]">{fw.description || "—"}</p>
                <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground font-mono">
                  <span>{fw.areas_count} aree</span>
                  <span>·</span>
                  <span>{fw.usage_count} assessment</span>
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <div className="flex gap-3">
                    <button onClick={() => openEdit(fw.id)} className="text-muted-foreground hover:text-primary" data-testid={`edit-framework-${i}`} title="Modifica"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => clone(fw.id)} className="text-muted-foreground hover:text-primary" data-testid={`clone-framework-${i}`} title="Duplica"><Copy className="w-4 h-4" /></button>
                    {fw.is_default || fw.usage_count > 0 ? (
                      <span className="text-muted-foreground/40" title={fw.is_default ? "Predefinito: non eliminabile" : "In uso: non eliminabile"}><Lock className="w-4 h-4" /></span>
                    ) : (
                      <button onClick={() => remove(fw.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-framework-${i}`} title="Elimina"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editId ? "Modifica framework" : "Nuovo framework"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Nome *</Label><Input data-testid="framework-name-input" className="mt-1 rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label className="data-label">Descrizione</Label><Input className="mt-1 rounded-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>

            <div className="flex items-center justify-between border-t border-border pt-3">
              <div className="data-label">Aree ({form.areas.length})</div>
              <span className={`text-xs font-mono font-bold ${totalWeight === 100 ? "text-[#00A859]" : "text-destructive"}`} data-testid="framework-weight-total">Somma pesi: {totalWeight}/100</span>
            </div>

            {form.areas.map((a, i) => (
              <div key={i} className="border border-border p-4 space-y-3" data-testid={`framework-area-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="data-label">Area {i + 1}</span>
                  {form.areas.length > 1 && <button onClick={() => rmArea(i)} className="text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>}
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-3"><Label className="data-label">Key *</Label><Input className="mt-1 rounded-none h-8 text-sm font-mono" value={a.key} onChange={(e) => updArea(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} placeholder="dati" /></div>
                  <div className="col-span-6"><Label className="data-label">Nome area</Label><Input className="mt-1 rounded-none h-8 text-sm" value={a.name} onChange={(e) => updArea(i, { name: e.target.value })} /></div>
                  <div className="col-span-3"><Label className="data-label">Peso (%)</Label><Input type="number" className="mt-1 rounded-none h-8 text-sm font-mono" value={a.weight} onChange={(e) => updArea(i, { weight: e.target.value })} /></div>
                </div>
                <div><Label className="data-label">Domanda guida</Label><Input className="mt-1 rounded-none h-8 text-sm" value={a.guide_question} onChange={(e) => updArea(i, { guide_question: e.target.value })} /></div>
                <div><Label className="data-label">Domande (una per riga)</Label><Textarea className="mt-1 rounded-none text-sm" rows={2} value={a.questions.join("\n")} onChange={(e) => updArea(i, { questions: e.target.value.split("\n") })} /></div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="data-label">Ancora 1</Label><Input className="mt-1 rounded-none h-8 text-xs" value={a.anchors[1]} onChange={(e) => updArea(i, { anchors: { ...a.anchors, 1: e.target.value } })} /></div>
                  <div><Label className="data-label">Ancora 3</Label><Input className="mt-1 rounded-none h-8 text-xs" value={a.anchors[3]} onChange={(e) => updArea(i, { anchors: { ...a.anchors, 3: e.target.value } })} /></div>
                  <div><Label className="data-label">Ancora 5</Label><Input className="mt-1 rounded-none h-8 text-xs" value={a.anchors[5]} onChange={(e) => updArea(i, { anchors: { ...a.anchors, 5: e.target.value } })} /></div>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={addArea} data-testid="add-area-btn"><Plus className="w-4 h-4 mr-1" /> Aggiungi area</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-framework-btn" className="rounded-none" onClick={save} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
